import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon } from '@radix-ui/react-icons';
import { Button, Card, Flex, Grid, Heading, Spinner } from '@radix-ui/themes';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import CheckboxGroupComponent from '../../components/inputs/CheckboxGroup/CheckboxGroupComponent';
import RadioGroupComponent from '../../components/inputs/RadioGroup/RadioGroupComponent';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import { useGetCoursesQuery } from '../../hooks/useGetCoursesQuery';
import { useGetDepartamentsQuery } from '../../hooks/useGetDepartamentsQuery';
import { useGetFacultiesQuery } from '../../hooks/useGetFacultiesQuery';
import { useGetScientificAreasQuery } from '../../hooks/useGetScientificAreasQuery';
import { useGetSubjectsQuery } from '../../hooks/useGetSubjectsQuery';
import { routes } from '../../routes';
import { createCourseSubject } from '../../services/school/courseSubject/createCourseSubject';
import { CreateCourseSubjectType } from '../../types/CourseSubject';
import { CreateCourseSubjectFormValidation } from './CreateCourseSubjectsFormValidation';

export default function CreateCourseSubject() {
  const navigate = useNavigate();
  const mutation = useMutation({ mutationFn: createCourseSubject, mutationKey: 'createCourseSubject' });

  const defaultValues: CreateCourseSubjectType = {
    idCourse: '',
    isCore: false,
    hasExam: false,
    semester: 0,
    level: 0,
    subjects: []
  }
  const { control, handleSubmit, setValue, getValues, formState: { errors }, watch } = useForm<CreateCourseSubjectType>({
    resolver: yupResolver(CreateCourseSubjectFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const watchSubjects = watch('subjects')

  const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400 });
  const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery(
    { PageNumber: 1, PageSize: 400, IdFaculty: getValues('idFaculty') },
    { enabled: !!getValues('idFaculty') }
  );
  const { data: courses, isLoading: isLoadingCourses, isError: errorCourses, refetch: refreshCourses } = useGetCoursesQuery({ PageNumber: 1, PageSize: 400, IdDepartament: getValues('idDepartament') },
    { enabled: !!getValues('idDepartament') }
  );

  const { data: scientificAreas, isLoading: isLoadingScientificAreas, isError: errorScientificAreas } = useGetScientificAreasQuery({ PageNumber: 1, PageSize: 400 });
  const { data: subjects, isLoading: isLoadingSubjects, isError: errorSubjects, refetch: refreshSubjects } = useGetSubjectsQuery({ PageNumber: 1, PageSize: 400, idScientificArea: getValues('idScientificArea') },
    { enabled: !!getValues('idScientificArea') }

  );


  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await mutation.mutateAsync({ ...data, subjects: watchSubjects.map(ws => { const subject = JSON.parse(ws); return subject.id }) });
      if (result) {
        toast.success("Plano do Curso criado");
        // navigate(routes.coursesSubjects);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleBack = () => {
    // navigate(routes.coursesSubjects);
  }

  const handleFacultyChange = (idFaculty: string) => {
    setValue('idFaculty', idFaculty);
    refreshDepartaments();
  };

  const handleDepartamentChange = (idDepartament: string) => {
    setValue('idDepartament', idDepartament);
    refreshCourses();
  };


  const handleScientificAreaChange = (idScientificArea: string) => {
    setValue('idScientificArea', idScientificArea);
    refreshSubjects();
  };

  const handleSubjectChange = (subject: any) => {
    const selectedItems = subject;

    const updatedSubjects = watchSubjects.filter((subject) =>
      selectedItems.includes(subject)
    );

    const acum = new Set([...updatedSubjects, ...selectedItems]);

    setValue("subjects", Array.from(acum));
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1'>
        <Flex direction='column' gap='4'>

          <Grid columns="2" gap="4" className="mb-6">

            <Card className="p-4">
              <Heading size="3" className="mb-4">Dados principais</Heading>
              <Grid columns="2" gap="4">

                <SelectComponent name="idFaculty" label="Unidade orgânica" control={control} errors={errors} style={{ width: '100%' }}
                  options={faculties?.items.map(faculty => ({
                    value: faculty.id ?? "",
                    label: faculty.description ?? ""
                  }))}
                  onSelect={handleFacultyChange}
                />

                <SelectComponent name="idDepartament" label="Departamento" control={control} errors={errors} style={{ width: '100%' }}
                  options={departaments?.items.map(departament => ({
                    value: departament.id ?? "",
                    label: departament.description ?? ""
                  }))}
                  onSelect={handleDepartamentChange}
                />

                <SelectComponent name="idCourse" label="Curso" control={control} errors={errors} style={{ width: '100%' }}
                  options={courses?.items.map(course => ({
                    value: course.id ?? "",
                    label: course.name ?? ""
                  }))}
                />

                <SelectComponent name="idScientificArea" label="Área científica" control={control} errors={errors} style={{ width: '100%' }}
                  options={scientificAreas?.items.map(scientificArea => ({
                    value: scientificArea.id ?? "",
                    label: scientificArea.description ?? ""
                  }))}
                  onSelect={handleScientificAreaChange}
                />

                <CheckboxGroupComponent
                  name="scientificAreaSubjects"
                  label="Disciplinas da área científica"
                  control={control}
                  errors={errors}
                  options={subjects?.items?.map(subject => ({ value: JSON.stringify(subject), label: subject?.description }))}
                  style={{ width: '100%' }}
                  handleItemChange={handleSubjectChange}
                  itemsError={errorSubjects}
                  itemsLoading={isLoadingSubjects}
                />

                <Flex gap="8">
                  <TextFieldComponent name="semester" label="Semestre" control={control} errors={errors} fieldProps={{ placeholder: 'Semestre' }} style={{ width: '100%' }} />
                  <TextFieldComponent name="level" label="Ano" control={control} errors={errors} fieldProps={{ placeholder: 'Ano' }} style={{ width: '100%' }} />
                </Flex>

                <RadioGroupComponent name="isCore" label="Tipo de disciplina" control={control} errors={errors} style={{ width: '100%' }}
                  options={[
                    { value: "true", label: "Nuclear" }
                    , { value: "false", label: "Opcional" }]}
                  fieldProps={{
                    style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
                  }}
                />

                <RadioGroupComponent name="hasExam" label="Tem exame?" control={control} errors={errors} style={{ width: '100%' }}
                  options={[
                    { value: "true", label: "Sim" }
                    , { value: "false", label: "Não" }]}
                  fieldProps={{
                    style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
                  }}
                />
              </Grid>
            </Card>
            <Card className="p-4">
              <Heading size="3" className="mb-4">Disciplinas do curso</Heading>

              <CheckboxGroupComponent
                name="subjects"
                label="Disciplinas do curso"
                control={control}
                errors={errors}
                options={watchSubjects?.map(subjectJsonString => {
                  const subject = JSON.parse(subjectJsonString);

                  return ({ value: JSON.stringify(subject), label: subject.description });
                })}
                style={{ width: '100%' }}
                handleItemChange={handleSubjectChange}
                itemsError={errorSubjects}
                itemsLoading={isLoadingSubjects}
              />
            </Card>

          </Grid>
          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button type='button' variant='soft' color='gray' onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> Voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'>
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Registar
            </Button>
          </Flex>
        </Flex>
      </Card>
    </form>
  );
}